/**
 * Column Configuration Version Manager
 * 
 * Handles versioning of column configurations across the application.
 * This ensures that when new columns are added, existing users' configurations
 * are properly updated to include the new columns.
 */

import {
  getPreference,
  setPreference,
  getRoleBasedKey,
  initializeRoleBasedPreferences,
  cleanupOldKeys // Add this import
} from './userPreferenceManger';
import { CURRENT_VERSIONS } from './sectionVersions';

/**  
 * Current version of column configurations for each section.  
 * This object defines the versioning for various sections in the application.  
 * Developers should update this whenever a new column is added or existing columns are modified.  
 * The structure generally follows the naming conventions.  
 */
// Re-export for consumers that imported CURRENT_VERSIONS from here directly.
export { CURRENT_VERSIONS } from './sectionVersions';

/**
 * Get the current version for a section, with fallback to base section
 * 
 * @param {string} sectionKey - The section identifier (e.g., 'inventory', 'order_0-1')
 * @returns {number} - The current version number for the section
 */
const getCurrentVersion = (sectionKey) => {
  // First try exact match
  if (CURRENT_VERSIONS[sectionKey] !== undefined) {
    return CURRENT_VERSIONS[sectionKey];
  }

  // Then try base section (before underscore)
  const baseSection = sectionKey.split('_')[0];
  if (CURRENT_VERSIONS[baseSection] !== undefined) {
    return CURRENT_VERSIONS[baseSection];
  }

  // Default to version 1
  return 1;
};

/**
 * Clears configuration for a specific section if it's outdated
 * 
 * @param {string} sectionKey - The section identifier (e.g., 'inventory', 'order')
 * @param {Array} defaultColumns - The default columns to set after clearing
 * @returns {boolean} - True if configuration was cleared (was outdated), false if not needed
 */
export const clearConfigIfOutdated = (sectionKey, defaultColumns) => {
  if (!sectionKey || !defaultColumns || !Array.isArray(defaultColumns)) {
    console.warn(`Invalid parameters passed to clearConfigIfOutdated`);
    return false;
  }

  // Initialize role-based preferences if needed
  initializeRoleBasedPreferences();

  const versionKey = `columnConfigVersion_${sectionKey}`;
  const storedVersion = parseInt(getPreference(versionKey, '0'), 10);
  const currentVersion = getCurrentVersion(sectionKey);

  // If the version is current, no reset needed
  if (storedVersion >= currentVersion) {
    console.log(`Configuration for ${sectionKey} is current (version ${storedVersion})`);
    return false;
  }

  console.log(`Clearing outdated configuration for ${sectionKey} (version ${storedVersion} → ${currentVersion})`);

  // Reset configuration to defaults and update to current version
  resetColumnConfig(sectionKey, defaultColumns);
  return true;
};

/**
 * Updates column configuration if the stored version is outdated
 * Now uses role-based preferences
 * 
 * @param {string} sectionKey - The section identifier (e.g., 'inventory', 'order')
 * @param {Array} currentColumns - The current complete list of columns
 * @returns {boolean} - True if configuration was updated, false otherwise
 */
export const updateColumnConfigIfNeeded = (sectionKey, currentColumns) => {
  if (!sectionKey || !currentColumns || !Array.isArray(currentColumns)) {
    console.warn(`Invalid parameters passed to updateColumnConfigIfNeeded: sectionKey=${sectionKey}, columns=${!!currentColumns}`);
    return false;
  }

  // Initialize role-based preferences if needed
  initializeRoleBasedPreferences();

  // Check if we have any old global keys that should be cleaned up
  const oldGlobalKeys = [
    `selectedColumns_${sectionKey}`,
    `columnOrder_${sectionKey}`,
    `fullColumnOrder_${sectionKey}`,
    `columnWidths_${sectionKey}`,
    `columnConfigVersion_${sectionKey}`
  ].filter(key => {
    // Only include keys that exist in localStorage but don't have role-based equivalents
    const hasGlobalKey = localStorage.getItem(key) !== null;
    const hasRoleBasedKey = localStorage.getItem(getRoleBasedKey(key)) !== null;
    return hasGlobalKey && hasRoleBasedKey; // Both exist, so we can clean up the global one
  });

  // Clean up old keys if they exist alongside role-based ones
  if (oldGlobalKeys.length > 0) {
    console.log(`Cleaning up ${oldGlobalKeys.length} old global keys for ${sectionKey}`);
    cleanupOldKeys(oldGlobalKeys);
  }

  const versionKey = `columnConfigVersion_${sectionKey}`;
  const storedVersion = parseInt(getPreference(versionKey, '0'), 10);
  const currentVersion = getCurrentVersion(sectionKey);

  // If stored version is current, no update needed
  if (storedVersion >= currentVersion) {
    return false;
  }

  console.log(`Updating ${sectionKey} column configuration from version ${storedVersion} to ${currentVersion}`);

  try {
    // Get all relevant storage keys for this section (now role-based)
    const selectedColumnsKey = `selectedColumns_${sectionKey}`;
    const columnOrderKey = `columnOrder_${sectionKey}`;
    const fullColumnOrderKey = `fullColumnOrder_${sectionKey}`;

    // Parse existing configurations or initialize with empty arrays
    const selectedColumns = getPreference(selectedColumnsKey, []);
    const columnOrder = getPreference(columnOrderKey, []);
    let fullColumnOrder = getPreference(fullColumnOrderKey, []);

    // If no full column order exists, initialize it with current columns
    if (!fullColumnOrder.length) {
      fullColumnOrder = [...currentColumns];
    }

    // Create sets of keys for easier comparison
    const currentColumnKeys = new Set(currentColumns.map(col => col.key));
    const existingKeys = new Set(fullColumnOrder.map(col => col.key));

    // 1. HANDLE COLUMN REMOVAL
    // Remove columns that no longer exist in the application
    // check for all selected columns and column order and fullColumnOrder and create set of removed columns
    const removedColumns = [...selectedColumns, ...columnOrder, ...fullColumnOrder].filter(col => !currentColumnKeys.has(col.key));

    if (removedColumns.length > 0) {
      console.log(`Removing ${removedColumns.length} columns that no longer exist in ${sectionKey}`);

      // Create a set of removed column keys for easier lookups
      const removedColumnKeys = new Set(removedColumns.map(col => col.key));

      // Filter out removed columns from fullColumnOrder
      fullColumnOrder = fullColumnOrder.filter(col => !removedColumnKeys.has(col.key));

      // Also remove from selectedColumns and columnOrder
      const updatedSelectedColumns = selectedColumns.filter(col => !removedColumnKeys.has(col.key));
      const updatedColumnOrder = columnOrder.filter(col => !removedColumnKeys.has(col.key));

      // Update preferences with filtered arrays
      setPreference(selectedColumnsKey, updatedSelectedColumns);
      setPreference(columnOrderKey, updatedColumnOrder);
    }

    // 2. HANDLE COLUMN ADDITION (existing code)
    // Add missing columns to fullColumnOrder
    const missingColumns = currentColumns.filter(col => !existingKeys.has(col.key));

    if (missingColumns.length) {
      console.log(`Adding ${missingColumns.length} new columns to ${sectionKey} configuration`);
      fullColumnOrder = [...fullColumnOrder, ...missingColumns];

      // Add missing columns to selectedColumns if they should be visible by default
      // For simplicity, we're adding all new columns to the selected columns
      const selectedKeys = new Set(selectedColumns.map(col => col.key));
      const missingSelectedColumns = missingColumns.filter(col => !selectedKeys.has(col.key));

      if (missingSelectedColumns.length && selectedColumns.length > 0) {
        // Preserve specific first column(s) if needed based on section
        const firstColumns = [];
        const remainingColumns = [...selectedColumns];

        if (sectionKey === 'inventory' || sectionKey.startsWith('order_')) {
          // For these sections, keep the first column in its position
          if (remainingColumns.length > 0) {
            firstColumns.push(remainingColumns.shift());
          }
        }

        // Update selected columns with new ones
        const updatedSelectedColumns = [
          ...firstColumns,
          ...remainingColumns,
          ...missingSelectedColumns
        ];

        setPreference(selectedColumnsKey, updatedSelectedColumns);
        setPreference(columnOrderKey, updatedSelectedColumns);
      }
    }

    // Save the updated full column order
    setPreference(fullColumnOrderKey, fullColumnOrder);

    // Update the version
    setPreference(versionKey, currentVersion.toString());

    return true;
  } catch (error) {
    console.error(`Error updating column configuration for ${sectionKey}:`, error);
    return false;
  }
};

/**
 * Resets column configuration to defaults for a specific section
 * Now uses role-based preferences
 * 
 * @param {string} sectionKey - The section identifier (e.g., 'inventory', 'order')
 * @param {Array} defaultColumns - The default columns for the section
 */
export const resetColumnConfig = (sectionKey, defaultColumns) => {
  const versionKey = `columnConfigVersion_${sectionKey}`;
  const selectedColumnsKey = `selectedColumns_${sectionKey}`;
  const columnOrderKey = `columnOrder_${sectionKey}`;
  const fullColumnOrderKey = `fullColumnOrder_${sectionKey}`;
  const columnWidthsKey = `columnWidths_${sectionKey}`;

  setPreference(selectedColumnsKey, defaultColumns);
  setPreference(columnOrderKey, defaultColumns);
  setPreference(fullColumnOrderKey, defaultColumns);
  setPreference(versionKey, getCurrentVersion(sectionKey).toString());

  // Remove column widths to reset to defaults
  const roleBasedWidthsKey = getRoleBasedKey(columnWidthsKey);
  localStorage.removeItem(roleBasedWidthsKey);

  console.log(`Reset ${sectionKey} column configuration to defaults for current role`);

  return true;
};
